import React from 'react';
import PropTypes from 'prop-types';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import NewsGridView from 'volto-agsus/blocks/NewsGrid/View';

// timeZone fixo (America/Sao_Paulo) é essencial: sem ele, getDate()/getMonth()
// usam o fuso do ambiente — Node SSR (UTC) e navegador (BRT) divergem em datas
// gravadas perto da meia-noite UTC, causando hydration mismatch (React #418).
const formatDateLong = (iso) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'America/Sao_Paulo',
    }).format(new Date(iso));
  } catch {
    return null;
  }
};

// Hero usa SOMENTE o campo hero_image (upload). Vazio → fundo navy (sólido).
// Não cai mais na imagem de preview (decisão Leo 26/05: navy estrito).
const resolveHeroImage = (content) => {
  const h = content?.hero_image;
  if (!h || typeof h !== 'object') return null;
  const s = h.scales || {};
  const url =
    s.huge?.download ||
    s.great?.download ||
    s.larger?.download ||
    s.large?.download ||
    s.preview?.download ||
    h.download;
  // Achata URL interna do backend → app-relative (gotcha SSR Volto).
  return url ? flattenToAppURL(url) : null;
};

// Conta palavras dentro dos blocks slate (texto editorial) pra estimar leitura.
const slateText = (nodes) => {
  if (!Array.isArray(nodes)) return '';
  return nodes
    .map(
      (n) => (n?.text || '') + (n?.children ? ' ' + slateText(n.children) : ''),
    )
    .join(' ');
};

const readingTime = (blocksData, layout) => {
  let words = 0;
  layout.forEach((id) => {
    const b = blocksData[id];
    if (b?.['@type'] === 'slate' && Array.isArray(b.value)) {
      words += slateText(b.value).trim().split(/\s+/).filter(Boolean).length;
    }
  });
  if (words < 30) return null; // textos curtos (cotações/editais) não exibem
  return `${Math.max(1, Math.round(words / 200))} min`;
};

const VIEW_DEBOUNCE_MS = 24 * 60 * 60 * 1000; // 24h por UID/browser

// Glifos oficiais das redes (Simple Icons, viewBox 0 0 24 24, CC0).
const SHARE = [
  {
    id: 'facebook',
    label: 'Facebook',
    url: (u) => `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    path: 'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z',
  },
  {
    id: 'x',
    label: 'X',
    url: (u) => `https://twitter.com/intent/tweet?url=${u}`,
    path: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    url: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    url: (u) => `https://api.whatsapp.com/send?text=${u}`,
    path: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z',
  },
];

const NewsItemView = ({ content }) => {
  // URL absoluta da página pra compartilhar (definida pós-mount, evita mismatch SSR).
  const [shareUrl, setShareUrl] = React.useState('');
  React.useEffect(() => {
    if (typeof window !== 'undefined')
      setShareUrl(encodeURIComponent(window.location.href));
  }, []);

  // Incrementa view_count quando a notícia carrega no client (debounce 24h/UID).
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const uid = content?.UID;
    const id = content?.['@id'];
    if (!uid || !id) return;
    try {
      const key = `agsus_viewed_${uid}`;
      const last = window.localStorage.getItem(key);
      const now = Date.now();
      if (last && now - parseInt(last, 10) < VIEW_DEBOUNCE_MS) return;
      const path = flattenToAppURL(id);
      fetch(`/++api++${path}/@view`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).catch(() => {});
      window.localStorage.setItem(key, String(now));
    } catch {
      /* localStorage bloqueado */
    }
  }, [content?.UID, content?.['@id']]);

  // Fora de /noticias, as seções "Veja Também" não se aplicam (a página é
  // registro documental) e o corpo ocupa coluna única.
  const contentPath = flattenToAppURL(content?.['@id'] || '');
  const isNoticia =
    contentPath === '/noticias' || contentPath.startsWith('/noticias/');

  const imageUrl = resolveHeroImage(content);
  const dateLong = formatDateLong(content?.effective || content?.created);
  const autoriaTexto = (content?.autoria_texto || '').trim();
  const autoriaImagens = (content?.autoria_imagens || '').trim();
  const autoriaAmbos = (content?.autoria_texto_imagens || '').trim();
  const eyebrow =
    (Array.isArray(content?.subjects) && content.subjects[0]) || 'Notícia';

  const blocksData = content?.blocks || {};
  const HIDDEN_TYPES = new Set([
    'title',
    'description',
    'featured-image',
    'newsitem-meta',
    'eyebrow',
  ]);
  const layout = (content?.blocks_layout?.items || []).filter((id) => {
    const t = blocksData[id]?.['@type'];
    return !HIDDEN_TYPES.has(t);
  });
  const filteredContent = {
    ...content,
    blocks: blocksData,
    blocks_layout: { items: layout },
  };

  const reading = readingTime(blocksData, layout);
  const subjects = content?.subjects || [];

  return (
    <article className="ag-news-view">
      {/* ── Hero: imagem full-bleed ou banda navy ── */}
      {imageUrl && (
        <header
          className="ag-news-view__hero ag-news-view__hero--image"
          style={{ backgroundImage: `url('${imageUrl}')` }}
        ></header>
      )}

      <div className="container">
        <div className="ag-news-view__head">
          <span className="ag-news-view__article-eyebrow">{eyebrow}</span>
          <h1 className="ag-news-view__title ag-news-view__title--article">
            {content.title}
          </h1>
        </div>
        {/* ── Barra de meta ── */}
        <div className="ag-news-view__metabar">
          {dateLong && (
            <div className="ag-news-view__meta-item">
              <strong>Publicado</strong>
              {dateLong}
            </div>
          )}
          {autoriaAmbos ? (
            <div className="ag-news-view__meta-item">
              <strong>Texto e imagens</strong>
              {autoriaAmbos}
            </div>
          ) : (
            <>
              {autoriaTexto && (
                <div className="ag-news-view__meta-item">
                  <strong>Texto</strong>
                  {autoriaTexto}
                </div>
              )}
              {autoriaImagens && (
                <div className="ag-news-view__meta-item">
                  <strong>Imagens</strong>
                  {autoriaImagens}
                </div>
              )}
            </>
          )}
          {reading && (
            <div className="ag-news-view__meta-item">
              <strong>Tempo de leitura</strong>
              {reading}
            </div>
          )}
          <div className="ag-news-view__share">
            <span className="ag-news-view__share-label">Compartilhar</span>
            {SHARE.map((s) => (
              <a
                key={s.id}
                className="ag-news-view__share-btn"
                href={shareUrl ? s.url(shareUrl) : undefined}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Compartilhar no ${s.label}`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d={s.path} />
                </svg>
              </a>
            ))}
          </div>
        </div>

        {/* ── Corpo em 2 colunas ── */}
        <div
          className={`ag-news-view__layout${isNoticia ? '' : ' ag-news-view__layout--single'}`}
        >
          <div className="ag-news-view__main">
            {content.description && (
              <p className="ag-news-view__lead">{content.description}</p>
            )}

            <div className="ag-news-view__body">
              <RenderBlocks content={filteredContent} />
            </div>

            {subjects.length > 0 && (
              <div className="ag-news-view__tags">
                <p className="ag-news-view__tags-label">Assuntos</p>
                <ul className="ag-news-view__tags-list">
                  {subjects.map((t) => (
                    <li key={t}>
                      <a
                        href={`/search?Subject=${encodeURIComponent(t)}`}
                        className="badge badge--outline ag-news-view__tag"
                      >
                        {t}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {isNoticia && (
            <aside className="ag-news-view__sidebar">
              <div className="ag-news-view__sidebar-card">
                <h2 className="ag-titulo-secao">Veja também</h2>
                <NewsGridView
                  data={{
                    ...(subjects.length > 0
                      ? {
                          mode: 'query',
                          query: {
                            query: [
                              {
                                i: 'portal_type',
                                o: 'plone.app.querystring.operation.selection.any',
                                v: ['News Item'],
                              },
                              {
                                i: 'Subject',
                                o: 'plone.app.querystring.operation.selection.any',
                                v: subjects,
                              },
                              // só notícias: editais e audiências também são News Item e têm assunto
                              {
                                i: 'path',
                                o: 'plone.app.querystring.operation.string.path',
                                v: '/noticias',
                              },
                            ],
                            sort_on: 'effective',
                            // O endpoint de querystring não reconhece sort_order_boolean — a lateral
                            // saía com as relacionadas mais ANTIGAS primeiro.
                            sort_order: 'descending',
                            // até 20 candidatas, para ordenar pela afinidade
                            // (mais assuntos em comum primeiro; ver
                            // relacionadas.js) e mostrar as 3 mais próximas
                            b_size: 20,
                          },
                          afinidade: subjects,
                        }
                      : { mode: 'auto', parentPath: '/noticias' }),
                    limit: 3,
                    columns: '1',
                    layout: 'sidebar',
                  }}
                  block="newsitem-sidebar"
                  excludePathname={content?.['@id']}
                />
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* ── Rodapé: as notícias mais recentes (22/10/2026). As relacionadas
             ficam na lateral; aqui saem as do mesmo assunto, para não repetir.
             Sem assunto, a lateral já mostra as 3 mais recentes, e o rodapé
             segue com as 3 seguintes. ── */}
      {isNoticia && (
        <div className="ag-news-view__related">
          <NewsGridView
            data={{
              mode: 'auto',
              title: 'Notícias mais recentes',
              parentPath: '/noticias',
              ...(subjects.length > 0
                ? { excluirAssuntos: subjects }
                : { pular: 3 }),
              limit: 3,
              columns: '3',
              layout: 'grid',
            }}
            block="newsitem-related"
            excludePathname={content?.['@id']}
          />
        </div>
      )}
    </article>
  );
};

NewsItemView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    autoria_texto: PropTypes.string,
    autoria_imagens: PropTypes.string,
    autoria_texto_imagens: PropTypes.string,
    hero_image: PropTypes.object,
    effective: PropTypes.string,
    created: PropTypes.string,
    image: PropTypes.object,
    preview_image_link: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    preview_caption_link: PropTypes.string,
    subjects: PropTypes.arrayOf(PropTypes.string),
    blocks: PropTypes.object,
    blocks_layout: PropTypes.object,
  }).isRequired,
};

export default NewsItemView;
